import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

export default function FinalExam() {
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/course").then(({ data }) => {
      setExam(data.final_exam);
      setAnswers(new Array(data.final_exam.length).fill(-1));
    });
  }, []);

  if (!exam) return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Loading final exam…</div>;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/progress/final-exam", { answers });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <button data-testid="exam-back" onClick={() => nav("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Final Examination</div>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        Comprehensive Final Exam
      </h1>
      <p className="text-slate-600 mt-3">{exam.length} questions covering all six modules. 80% required to pass.</p>

      {result && (
        <div className={`mt-8 border rounded-sm p-6 ${result.passed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
          <div className="flex items-start gap-4">
            {result.passed ? <CheckCircle2 className="h-10 w-10 text-green-700" /> : <XCircle className="h-10 w-10 text-red-700" />}
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
                {result.passed ? "Final Exam Passed" : "Not Yet — Try Again"}
              </h2>
              <p className="text-slate-700 mt-2">
                Score: <span className="font-mono font-bold">{Math.round(result.score * 100)}%</span> ({result.correct} of {result.total})
              </p>
              {result.passed && (
                <Link to="/certificate" data-testid="final-pass-to-cert" className="inline-block mt-4 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-sm font-medium text-sm">
                  Proceed to Acknowledgement Letter →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 space-y-6">
        {exam.map((q, qi) => (
          <div key={qi} data-testid={`final-q-${qi}`} className="border border-slate-200 rounded-sm p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Question {qi + 1}</div>
            <div className="text-base font-semibold text-slate-900 mt-2">{q.q}</div>
            <div className="mt-4 space-y-2">
              {q.options.map((o, oi) => (
                <label key={oi} data-testid={`final-q-${qi}-opt-${oi}`}
                  className={`flex items-start gap-3 px-4 py-3 border rounded-sm cursor-pointer transition-colors ${
                    answers[qi] === oi ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                  }`}>
                  <input type="radio" name={`fq${qi}`} className="mt-1"
                    checked={answers[qi] === oi}
                    onChange={() => setAnswers((a) => a.map((x, i) => (i === qi ? oi : x)))} />
                  <span className="text-sm text-slate-700">{o}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-900">Back to dashboard</Link>
        <button
          data-testid="submit-final-btn"
          disabled={answers.some((a) => a === -1) || submitting}
          onClick={submit}
          className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-sm font-medium disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit final exam"}
        </button>
      </div>
    </div>
  );
}
